import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Menu, LogOut } from 'lucide-react';
import Image from './Image';
import final_logo from '../../assets/images/my-code-dragon.png';
import CustomSelect from '../common/CustomSelect';
import { logout } from '../../redux/counterSlice';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { useLogoutUserMutation } from '../../redux/apiSlice';

interface NavLink {
  to: string;
  label: string;
  dropdown?: {
    value: string;
    label: string;
    link: string;
    showOnRoutes?: string[];
  }[];
  showOnRoutes?: string[];
}

const Navbar: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const isAuthenticated = useSelector(
    (state: RootState) => state.app.user.isAuthenticated,
  );

  const [logoutUser, { isLoading }] = useLogoutUserMutation();

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  const hideLinksRoutes = [
    '/login',
    '/forget-password',
    '/signup',
    '/contactus',
    '/affiliate',
    '/terms',
    '/privacy',
    '/cookies',
    '/faq',
    '/blog',
    '/blog/page/:pageNumber',
    '/blog/category/:categorySlug',
    '/blog/category/:categorySlug/page/:pageNumber',
    '/blog/:slug',
    '/payment',
    // '/reset-password',
  ];

  const shouldHideLinks = hideLinksRoutes.includes(location.pathname);
  const shouldMoveLogoLeft =
    isAuthenticated || location.pathname === '/courses';

  const leftLinks: NavLink[] = [
    // {
    //   to: '',
    //   label: 'Overview',
    //   dropdown: [{ value: 'student', label: 'Students', link: '/overview' }],
    //   showOnRoutes: ['/'],
    // },
    {
      to: '/pricing/annually',
      label: 'Pricing',
      // dropdown: [
      //   { value: 'students', label: 'Students', link: '/pricing/annually' },
      // ],
      showOnRoutes: ['/'],
    },
    { to: '/courses', label: 'Courses', showOnRoutes: ['/'] },
  ];

  const rightLinks: NavLink[] = [
    { to: '/login', label: 'Log In', showOnRoutes: ['/'] },
    { to: '/signup', label: 'Sign Up', showOnRoutes: ['/'] },
    {
      to: '/profile',
      label: 'Profile',
      dropdown: [
        // {
        //   value: 'reset-password',
        //   label: 'Reset Password',
        //   link: '/reset-password',
        // },
      ],
      showOnRoutes: [
        '/courses',
        '/courses/coding',
        '/profile',
        '/profile/subscription',
        '/student-dashboard',
      ],
    },
    {
      to: '/student-dashboard',
      label: 'Student Dashboard',
      showOnRoutes: [
        '/courses',
        '/courses/coding',
        '/profile',
        '/profile/subscription',
        '/student-dashboard',
      ],
    },
    {
      to: '/courses',
      label: 'My Courses',
      dropdown: [
        {
          value: 'my courses',
          label: 'My Courses',
          link: '/courses',
          showOnRoutes: ['/courses/coding'],
        },
        {
          value: 'exit Course',
          label: 'Exit Course',
          link: '/courses',
          showOnRoutes: ['/courses/coding'],
        },
      ],
      showOnRoutes: [
        '/courses',
        '/courses/coding',
        '/profile',
        '/profile/subscription',
        '/student-dashboard',
      ],
    },
  ];

  const pricingOptions = [
    { to: '/pricing/annually', label: 'Annual' },
    { to: '/pricing/twoyears', label: 'Two Year' },
  ];

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  const gotoHome = () => {
    closeMenu();
    const currentPath = rightLinks.find(
      (link) => link.to === location.pathname,
    );
    if (currentPath?.showOnRoutes?.includes('/courses') && isAuthenticated) {
      navigate('/courses');
    } else {
      navigate('/');
    }
  };

  const renderLeftLinks = () => {
    return leftLinks.map((link, index) => {
      if (link.showOnRoutes?.some((route) => location.pathname === route)) {
        return (
          <div key={index} className="relative group">
            <Link
              to={link.to}
              className="text-3xl font-bold hover:text-yellow-300 transition-colors hover:no-underline md:text-3xl text-xl"
              onClick={closeMenu}
            >
              {link.label}
            </Link>
            {link.dropdown && (
              <div className="hidden md:block">
                <CustomSelect options={link.dropdown} />
              </div>
            )}
          </div>
        );
      }
      return null;
    });
  };

  const renderRightLinks = () => {
    if (location.pathname.startsWith('/pricing')) {
      return pricingOptions.map((option, index) => (
        <Link
          key={index}
          to={option.to}
          className={`text-2xl font-bold transition-colors hover:no-underline md:text-2xl text-lg ${
            location.pathname.startsWith(option.to)
              ? 'bg-white text-black rounded-full p-2 py-[1px]'
              : 'hover:text-yellow-300'
          }`}
          onClick={closeMenu}
        >
          {option.label}
        </Link>
      ));
    }

    // Check if current path matches /courses/:courseId pattern
    const isCourseDetailPage = /^\/courses\/[^/]+/.test(location.pathname);

    return rightLinks.map((link, index) => {
      const isActive =
        location.pathname === link.to ||
        (link.to === '/profile' && location.pathname.startsWith('/profile'));

      // Show profile navigation items on course detail pages too
      if (
        link.showOnRoutes?.some(
          (route) =>
            location.pathname === route ||
            (isCourseDetailPage && route === '/courses'),
        )
      ) {
        const dropdownOptions = link.dropdown?.filter((option) => {
          if (!option.showOnRoutes) {
            return true;
          } else {
            return option.showOnRoutes?.some(
              (route) =>
                location.pathname === route ||
                (isCourseDetailPage && route === '/courses/coding'),
            );
          }
        });
        return (
          <div key={index} className="relative group">
            <Link
              to={link.to}
              className={`text-3xl font-bold transition-colors hover:no-underline md:text-3xl text-lg ${
                isActive
                  ? 'bg-white text-black rounded-full p-3'
                  : 'hover:text-yellow-300'
              }`}
              onClick={closeMenu}
            >
              {link.label}
            </Link>
            {dropdownOptions?.length ? (
              <div className="hidden md:block">
                <CustomSelect options={dropdownOptions} onClick={closeMenu} />
              </div>
            ) : null}
          </div>
        );
      }
      return null;
    });
  };

  return (
    <nav className="h-[6.25rem] text-white flex items-center justify-between bg-[#004aad] lg:px-20 md:px-8 px-4 relative">
      {/* Left Section */}
      <div
        className={`flex items-center ${shouldMoveLogoLeft ? 'gap-2 lg:gap-6' : 'gap-0'}`}
      >
        {/* Logo */}
        <div
          className={`${
            shouldMoveLogoLeft
              ? 'flex-shrink-0 relative mt-[-0.94rem] z-10 w-[12.5rem]'
              : 'absolute left-1/2 top-[-0.94rem] transform -translate-x-1/2 z-10 w-[12.5rem]'
          }`}
        >
          <div
            onClick={() => gotoHome()}
            className="cursor-pointer h-[6.88rem] w-[12.5rem] overflow-hidden"
          >
            <Image
              src={final_logo}
              alt="my dragon logo"
              className="!w-[12.5rem] !h-[8.31rem] object-contain"
            />
          </div>
        </div>

        {/* Left Links - show for all users */}
        <div className="hidden lg:flex gap-6 items-center">
          {!shouldHideLinks && renderLeftLinks()}
        </div>
      </div>

      {/* Center Logo for users when logo should be centered */}
      {!shouldMoveLogoLeft && (
        <div className="flex-1 flex justify-center">
          {/* Logo is already positioned absolutely in left section */}
        </div>
      )}

      {/* Right Links */}
      <div className="hidden lg:flex gap-6 items-center">
        {!shouldHideLinks && renderRightLinks()}
        {/* Logout Button for Desktop */}
        {isAuthenticated && !shouldHideLinks && (
          <button
            onClick={() => {
              logoutUser();
              dispatch(logout());
              navigate('/');
            }}
            className="text-3xl font-bold transition-colors hover:text-yellow-300 flex flex-col items-center gap-1"
            disabled={isLoading}
            title="Logout"
          >
            {isLoading ? (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
            ) : (
              <LogOut size={24} />
            )}
            <span className="text-sm font-normal">Logout</span>
          </button>
        )}
      </div>

      {/* Menu Icon for smaller screens and iPad */}
      {!shouldHideLinks && (
        <div className="lg:hidden ml-auto">
          <button onClick={toggleMenu}>
            <Menu className="text-white" size={32} />
          </button>
        </div>
      )}

      {/* Dropdown Menu for smaller screens */}
      {isMenuOpen && (
        <div
          className="fixed inset-0 top-[6.25rem] bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={closeMenu}
        >
          <div
            className="absolute top-0 right-0 w-full max-w-sm bg-[#004aad] shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex flex-col p-6 space-y-6">
              {/* Navigation Links */}
              <div className="flex flex-col space-y-4">
                {!shouldHideLinks && renderLeftLinks()}
                {!shouldHideLinks && renderRightLinks()}
              </div>

              {/* Logout Button at Bottom */}
              {isAuthenticated && (
                <div className="border-t border-white border-opacity-20 pt-6 mt-6">
                  <button
                    onClick={() => {
                      logoutUser();
                      closeMenu();
                      dispatch(logout());
                      navigate('/');
                    }}
                    className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-3 px-4 rounded-lg transition-colors duration-200 flex items-center justify-center space-x-2"
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <>
                        <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                        <span>Logging out...</span>
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-5 h-5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                          />
                        </svg>
                        <span>Logout</span>
                      </>
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
